// Lets any component ask the header to open its login/signup dropdown,
// without needing shared React state — same lightweight pub/sub pattern
// as toast.js. SiteHeader subscribes once and opens its own dropdown;
// callers just fire requestLogin() and don't need to know anything about
// where the header actually is on the page.

let loginListeners = new Set();

export function requestLogin() {
  loginListeners.forEach((fn) => fn());
}

export function onLoginRequested(fn) {
  loginListeners.add(fn);
  return () => loginListeners.delete(fn);
}

// AuthForm (the actual login/signup form, shared and rendered wherever
// login is needed — the header's dropdown, or a "you need to be logged
// in" modal elsewhere on the page) calls this the moment a login/signup
// succeeds. Every component tracking its own copy of the logged-in user
// (SiteHeader, App.jsx) subscribes so they all update together, regardless
// of which particular AuthForm instance the user actually logged in
// through.
let authChangeListeners = new Set();

// Remembers the most recent announcement so a component that (re)mounts
// after it fired still gets the current state immediately, not just
// future changes — see onAuthChanged below. `undefined` specifically means
// "nothing announced yet" (distinct from a genuine logged-out
// {user: null, token: null} state), so a subscriber before the first real
// announcement isn't called with a premature "logged out" that doesn't
// reflect anything real yet.
let lastAuthState;

export function announceAuthChange(user, token) {
  lastAuthState = { user, token };
  authChangeListeners.forEach((fn) => fn(user, token));
}

export function onAuthChanged(fn) {
  authChangeListeners.add(fn);
  // Deliver the current state right away, not just changes from here on
  // — without this, a component that (re)mounts after the last
  // announcement (e.g. App.jsx after navigating away to /profile and
  // back — it fully unmounts and remounts across that, unlike
  // SiteHeader which stays mounted globally the whole time) would start
  // from nothing and never find out the actual current state until some
  // future auth event happens to fire, which may never come.
  if (lastAuthState) fn(lastAuthState.user, lastAuthState.token);
  return () => authChangeListeners.delete(fn);
}
